import { ref } from 'vue'

function isNumberArray(value: unknown): value is number[] {
  return Array.isArray(value) && value.every((entry) => typeof entry === 'number')
}

function readCollapsedIds(storageKey: string): Set<number> {
  try {
    const raw = localStorage.getItem(storageKey)
    if (!raw) return new Set()

    const parsed: unknown = JSON.parse(raw)
    if (!isNumberArray(parsed)) return new Set()

    return new Set(parsed)
  } catch {
    return new Set()
  }
}

function writeCollapsedIds(storageKey: string, ids: Set<number>): void {
  try {
    localStorage.setItem(storageKey, JSON.stringify([...ids]))
  } catch {
    // Storage may be unavailable (e.g. disabled, full, or private browsing) - ignore silently.
  }
}

/**
 * Tracks which numeric IDs are "collapsed" for a given UI section, persisting only the
 * collapsed IDs per browser in localStorage. Items are expanded by default: an ID that
 * has never been toggled (or is not present in storage) is treated as expanded.
 */
export function useCollapsedIds(storageKey: string) {
  const collapsedIds = ref<Set<number>>(readCollapsedIds(storageKey))

  function isCollapsed(id: number): boolean {
    return collapsedIds.value.has(id)
  }

  function toggle(id: number): void {
    const next = new Set(collapsedIds.value)
    if (next.has(id)) {
      next.delete(id)
    } else {
      next.add(id)
    }
    collapsedIds.value = next
    writeCollapsedIds(storageKey, next)
  }

  return { isCollapsed, toggle }
}
